import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { mxn } from '@/lib/formato';
import { cn } from '@/lib/utils';

import type { StoreMembership } from './queries';

/** Tarjeta de la tienda (skill §14): franja de nombre, precio en bloque verde y listón "Ahorra". */
export function TarjetaMembresia({ m }: { m: StoreMembership }) {
  const [abierta, setAbierta] = useState(false);
  const ahorra = Number(m.savings) > 0;
  const comprable = !m.soldOut && !m.comingSoon;
  return (
    <article
      data-card
      className={cn(
        'card-vidrio card-vidrio-hover relative flex flex-col overflow-hidden',
        m.highlighted && 'md:col-span-2 xl:col-span-1',
        m.pulse && comprable && 'pulse-cta',
      )}
    >
      {ahorra && (
        <div
          className="pointer-events-none absolute top-0 right-0 z-10 h-36 w-36 overflow-hidden"
          aria-hidden
        >
          {/* Listón de esquina: 224 px a 45° alrededor de su centro. La animación va en el span:
              animate.css fija transform-origin en el elemento animado y movería la rotación. */}
          <div className="absolute top-9 -right-14 w-56 origin-center rotate-45 bg-amarillo-flash py-1 text-center text-xs font-extrabold whitespace-nowrap text-noche-950 shadow-lg">
            <span className="animate__animated animate__fadeInDown inline-block">
              AHORRA {mxn(m.savings).replace('.00', '')}
            </span>
          </div>
        </div>
      )}
      <header className="bg-azul-600 px-5 py-3 pr-24 text-white">
        <h2 className="text-base leading-tight font-extrabold">{m.name}</h2>
        {m.highlighted && <span aria-label="Presencial">⭐⭐⭐</span>}
      </header>
      <div className="flex-1 p-5">
        <button
          type="button"
          onClick={() => setAbierta((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-extrabold tracking-wide text-texto-2 uppercase"
          aria-expanded={abierta}
        >
          ¿Qué contiene?{' '}
          <ChevronDown size={16} className={cn('transition-transform', abierta && 'rotate-180')} />
        </button>
        {abierta && (
          <ul className="mt-3 space-y-1.5 text-sm text-texto-2">
            {m.description.map((d) => (
              <li key={d} className="flex gap-2">
                <span className="text-verde-texto">✓</span>
                {d}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-xs text-texto-3">Vigencia: {m.durationMonths} meses</p>
        {m.lastSpots !== null && comprable && (
          <p className="mt-2 text-xs font-extrabold text-rojo-texto">
            ¡Últimos {m.lastSpots} lugares!
          </p>
        )}
      </div>
      {m.soldOut && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center">
          <span className="sello">Agotada</span>
        </div>
      )}
      <footer className="bg-verde-500 px-5 py-4 text-noche-950">
        {ahorra && <p className="text-sm line-through opacity-70">{mxn(m.regularPrice)}</p>}
        <p className="text-3xl font-extrabold tabular">{mxn(m.finalPrice)}</p>
        <p className="text-xs font-bold">MXN · Pesos mexicanos</p>
        {comprable ? (
          <Link
            to={`/pagar/${String(m.id)}`}
            className="btn-cs mt-3 w-full bg-noche-950 text-white hover:bg-noche-900"
          >
            ¡Inscríbete hoy!
          </Link>
        ) : (
          <span className="btn-cs mt-3 w-full cursor-not-allowed bg-noche-950/40 text-noche-950">
            {m.comingSoon ? 'Próximamente' : 'Cupos agotados'}
          </span>
        )}
      </footer>
    </article>
  );
}
